import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { Scenario } from '../../../views/scenario/model/scenario.model';
import { BaseGranularity } from '../base-granularity/base-granularity';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { IProduct } from 'src/app/views/product/model/product.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from "@ngrx/store";
import {
  selectFilters,
  selectFiltersByDashboardAndScenarioId
} from "../../../store/selectors/reporting.selectors";
import { BehaviorSubject, combineLatest, filter, lastValueFrom, map, Subject, switchMap } from "rxjs";
import { Helpers } from "../../shared/helpers";
import { AccountService } from '../../services/account.service';
import {
  PRODUCT_RESET_FILTERS,
  SCENARIO_RESET_FILTERS,
  SHOW_INTERNAL_FILTER
} from '../../app.constants';
import { take } from "rxjs/operators";
import { selectAllScenarios } from "../../../store";
import { ThemeService } from "../../services/theme.service";
import { DrillMenuEvent } from "@looker/embed-sdk";
import { DrawerService } from "../../../views/milestones/service/drawer.service";
import {
  UaOptimisationDashboardsComponent
} from "../../../views/ua/ua-optimisation-dashboards/ua-optimisation-dashboards.component";

export interface Preset {
  label: string;
  type: string;
  icon: string;
  filters: object;
}

@Component({
  selector: 'app-looker-granularity',
  templateUrl: './looker-granularity.component.html',
  styleUrls: ['./looker-granularity.component.scss'],
  standalone: false
})
export class LookerGranularityComponent extends BaseGranularity implements OnInit {
  @Input('chart') chart: string;
  @Input('scenario') scenario: Scenario;
  @Input('product') product: IProduct;
  @Input('isProductChart') isProductChart: boolean;
  @Input('model') model: string;
  @Input('hasGranularity') set hasGranularity(value: boolean) {
    this._hasGranularity = value;
    this.setHiddenFilters();
  }
  get hasGranularity(): boolean {
    return this._hasGranularity;
  }

  @Input('preset') preset: string;

  @Output() presetChange = new EventEmitter<Preset>();
  @Output() hasScenarioFilter = new EventEmitter<boolean>();

  protected activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private store: Store = inject(Store);
  private accountService: AccountService = inject(AccountService);
  private drawerService: DrawerService = inject(DrawerService);

  _params: Params = {};
  _hasGranularity: boolean = true;
  public isDailyGranularity: boolean;
  public presetSet: boolean = false;
  private destroyRef = inject(DestroyRef);
  isInternal: boolean;
  filters$ = new Subject<object>();
  theme$ = this.themeService.getCurrentTheme();
  hiddenFilters$ = new BehaviorSubject<string[]>(null);
  onChanges = new Subject<SimpleChanges>();

  constructor(
    public themeService: ThemeService
  ) {
    super();
    this.activatedRoute.queryParams
      ?.pipe(
        takeUntilDestroyed(this.destroyRef),
        // If the preset exists in route data set it from there otherwise it will be overridden from queryParams
        switchMap(params => this.activatedRoute.data.pipe(
          map(({ preset }) => ({ ...params, ...preset && !params.preset && { preset } })))
        ),
        switchMap(params => this.store.select(selectFilters).pipe(
          map(filters => ({ ...params, ...filters })))
        )
      ).subscribe(params => {
      this._params = params
    })
  }

  ngOnInit() {
    this.setPreset();

    combineLatest([
      this.accountService.hasAnyAuthority(['ROLE_INTERNAL'])
    ]).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(async ([isInternal]) => {
      this.isInternal = isInternal;
      await this.loadDashboard();
    });

    this.onChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(this.handleUpdates.bind(this));
  }

  public async loadDashboard() {
    this.setHiddenFilters();
    if (this.scenario) {
      await this.loadScenarioDashboard();
    } else if (this.product?.id) {
      await this.loadProductDashboard();
    }
  }

  private async loadScenarioDashboard() {
    const filters = await this._filters();
    this.filters$.next(filters);
  }

  private async loadProductDashboard() {
    const filters = {
      ...await this._filters(),
      ...this._productFilters(),
      ...(typeof this.isInternal === 'boolean' && {
          [SHOW_INTERNAL_FILTER]: (this.isInternal ? 'yes' : 'no')
        }
      ),
    };
    this.filters$.next(filters);
  }

  get dashboard() {
    let dashboard = this.chart;
    if (((!this.presetSet && (this._params.preset && !this.isExcludedPreset(this._params.preset))) || !this.isExcludedPreset(this.selectedPreset.label)) && this.hasGranularity) {
      dashboard = `${ this.chart }_non_daily`;
    }
    return dashboard;
  }

  async _filters() {
    // We need to wait for all scenarios to load before we can load direct to particular dashboards.
    const scenarios = await lastValueFrom(this.store.select(selectAllScenarios).pipe(filter((scenarios) => !!scenarios && scenarios.length > 0), take(1)))

    const filters = await lastValueFrom(this.store.select(selectFiltersByDashboardAndScenarioId(this.dashboard, this.scenario?.id, scenarios)).pipe(take(1)))
    const presetFilters = this.selectedPreset.filters
    if (!Helpers.hasDefaultDateRange(this._params)) {
      delete presetFilters['Date Range'];
    }
    return {
      ...this._params,
      ...(this.hasGranularity && this.presetSet && presetFilters),
      ...filters,
      Scenario: this.scenario?.id.toString() || this._params.Scenario || ''
    }
  }

  private _productFilters() {
    if (typeof this.product !== 'number') {
      const product = this.product as IProduct;
      if ('product_retention_curve' === this.dashboard) {
        return Helpers.getRetentionCurveFilter(product.metrics.sourceRange.end);
      }
    }
    return {
      Product: (this.product?.id || this.product).toString()
    }
  }

  async onChange($event: Preset) {
    this.selectedPreset = $event;
    this.presetChange.emit(this.selectedPreset);
    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        ...this._params,
        ...{
          preset: this.selectedPreset.label.toLowerCase(),
        }
      }
    })

    if (this.isDailyGranularity !== this.isExcludedPreset($event.label)) {
      this.setDailyGranularity();
      await this.loadDashboard();
    } else {
      this.filters$.next($event.filters);
    }
  }

  updateFilters(filters = {}) {
    this.filters$.next(filters);
  }

  private setPreset() {
    const preset = this.preset || this._params.preset;
    if (preset) {
      this.selectedPreset = this.getPresetByLabel(preset) ?? this.granularityOptions[0];
    }
    this.setDailyGranularity();
    this.presetSet = true;
  }

  private isExcludedPreset(preset: string) {
    const excludedPresets = this.isProductChart ? ['Day', 'Week'] : ['Day'];
    return excludedPresets.includes(preset);
  }

  setDailyGranularity() {
    this.isDailyGranularity = this.selectedPreset.label === 'Day';
  }

  private getPresetByLabel(label: string): Preset {
    return this.granularityOptions.find(preset => preset.label.toLowerCase() === label);
  }

  protected readonly SCENARIO_RESET_FILTERS = SCENARIO_RESET_FILTERS;
  protected readonly PRODUCT_RESET_FILTERS = PRODUCT_RESET_FILTERS;

  private setHiddenFilters() {
    let filters = [];
    if (this.scenario && this.hasGranularity) {
      filters = this.scenarioHiddenFilters;
    } else if (this.product?.id && this.hasGranularity) {
      filters = this.productWithGranularityHiddenFilters;
    } else if (this.product?.id) {
      filters = this.productHiddenFilters;
    } else if (this.scenario) {
      filters = ['Scenario'];
    }
    this.hiddenFilters$.next([SHOW_INTERNAL_FILTER, ...filters])
  }

  // This method gets called before the ngOnInit so we moving it to a subject to only execute after the component is initialized/changed
  ngOnChanges(changes): void {
    this.onChanges.next(changes);
  }

  private handleUpdates(changes: SimpleChanges) {
    const product: SimpleChange = (changes.product);
    if (changes.preset) {
      this.selectedPreset = this.getPresetByLabel(changes.preset.currentValue) ?? this.granularityOptions[0];
    }
    if (product) {
      this.loadProductDashboard();
    }
  }

  handleDrillMenuClick($event: DrillMenuEvent) {
    const { url } = $event;
    const urlParams = URL.parse(url);

    // convert urlParams.searchParams to a map and capitalize the keys.
    const filters = {
      ...Object.fromEntries(Array.from(urlParams.searchParams.entries()).map(([key, value]) => [key.charAt(0).toUpperCase() + key.slice(1), value])),
      ...{
        Product: this.product?.id || this._params.Product,
        Scenario: this.scenario?.id || this._params.Scenario
      }
    }

    this.drawerService.openBottomSheet(UaOptimisationDashboardsComponent, {
      data: {
        filters: filters,
      },
    })
  }
}
